import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useSpaces } from "../context/SpaceProvider";
import useRetryFetch from "./useRetryFetch";
import { listCompanionMessages, CompanionMessage } from "../api/companionMessages";
import { createTakeaway, editTakeaway } from "../api/userTakeaways";
import { extractItems, ExtractItemsResponse } from "../api/extractAI";
import { changeSpaceStatus } from "../api/identitySpaces";
import { trackHomeEvent } from "../lib/homeAnalytics";
import { buildReflectionTakeaway } from "../lib/reflectionCapture";
import { dueInDays } from "../components/home/TodayCard";
import { queryClient } from "../lib/queryClient";
import {
  DRIP_STORAGE_KEY, DripState, applyAnswer, applySkip, currentReflection,
  emptyDripState, extractQuestion, localDateKey, syncQueue,
} from "../lib/reflectionDrip";

const readState = (): DripState => {
  try {
    const raw = localStorage.getItem(DRIP_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.version === 1) return parsed as DripState;
    }
  } catch {}
  return emptyDripState();
};

const writeState = (s: DripState) => {
  try { localStorage.setItem(DRIP_STORAGE_KEY, JSON.stringify(s)); } catch {}
};

export interface ReflectionContent {
  spaceId: string;
  spaceName: string;
  spaceColor: string;
  identityLabel: string | null;
  framing: string;
  question: string | null;
  index: number;
  total: number;
  /** id of the space that would surface next in DISPLAY order (walk-aware —
   *  the same lookup that produces `next` below, kept as a raw id so `skip()`
   *  can name it as the deferral target instead of the literal queue front). */
  nextSpaceId: string | null;
  next: { name: string; identityLabel: string | null } | null;
}

/** Today's single area-reflection card (spec §3.3), backed by the local drip
 *  queue in `lib/reflectionDrip`. Field names verified against the live
 *  types: `IdentitySpace` (`_id,name,identityLabel,color,status,sortOrder,
 *  createdOn`) and `CompanionMessage` (`fiSpace,messageType,content`) — both
 *  match this hook's field access as written, no renames needed. */
export function useReflectionDrip() {
  const retryFetch = useRetryFetch();
  const { t, i18n } = useTranslation();
  const { spaces } = useSpaces();
  const [state, setState] = useState<DripState>(readState);

  const update = useCallback((next: DripState) => {
    writeState(next);
    setState(next);
  }, []);

  // Frozen snapshot of the just-answered reflection, stamped with the local
  // day it was frozen on. `answer()` advances the drip queue immediately (so
  // the NEXT area can unlock), which would otherwise flip `liveContent` to
  // the next space (or null) in the same tick — unmounting <ReflectionCard/>
  // (whose render is gated on this hook's `content` being non-null) before
  // it ever paints its own "saved" state, or silently swapping in a second
  // question in the same sitting. Freezing the answered snapshot keeps the
  // card mounted showing its own saved/tomorrow-hook view; it expires at
  // local midnight (the stamped day no longer matching `localDateKey()`)
  // rather than winning forever in a continuously-mounted session (pinned
  // tab / installed PWA) — the live value takes back over once expired.
  const [frozen, setFrozen] = useState<{ content: ReflectionContent; day: string } | null>(null);

  // A backgrounded tab/PWA doesn't re-render on its own when the calendar
  // day rolls over — bump this on visibility/focus regain so the frozen
  // snapshot's expiry (checked fresh against `localDateKey()` below, on
  // every render) gets re-evaluated as soon as the user comes back.
  const [, forceRecheck] = useState(0);
  useEffect(() => {
    const reEvaluate = () => forceRecheck((n) => n + 1);
    document.addEventListener("visibilitychange", reEvaluate);
    window.addEventListener("focus", reEvaluate);
    return () => {
      document.removeEventListener("visibilitychange", reEvaluate);
      window.removeEventListener("focus", reEvaluate);
    };
  }, []);

  const eligibleSpaces = useMemo(
    () => (spaces ?? []).filter((s) => s.status === "created" || s.status === "active"),
    [spaces]
  );

  // Finding 1 fix: the drip needs each eligible space's WELCOME message
  // specifically — it's the oldest companion message in a space, and the
  // very first thing this hook's queue head shows. `useAllSpacesCompanionMessages`
  // only fetches the latest 3 messages per space (it's built for the home
  // feed), so once a space's companion generates a 4th message the welcome
  // silently rotates out of that window and this hook found it "missing"
  // forever — the drip went dark permanently for that space. Fetch it here
  // instead, directly, filtered server-side by messageType so it's never
  // subject to that recency window. Constructed the same way as
  // useAllSpacesCompanionMessages's useQueries (one query per eligible
  // space, keyed for React Query's own per-space caching/dedup).
  const welcomeQueries = useQueries({
    queries: eligibleSpaces.map((space) => ({
      queryKey: ["space-welcome", space._id],
      queryFn: () => listCompanionMessages(retryFetch, space._id, 1, 0, "welcome"),
      // Welcomes are immutable once generated (a one-time AI message fired
      // on space creation), but a brand-new space's welcome can still be
      // mid-generation at first load — a finite staleTime (mirroring
      // useAllSpacesCompanionMessages) lets a later refetch self-heal that
      // "not yet generated" result instead of caching it dark for the
      // session, rather than genuinely refetching a resolved message.
      staleTime: 5 * 60 * 1000,
      enabled: !!space._id,
    })),
  });

  const welcomeBySpace = useMemo(() => {
    const map = new Map<string, { isLoading: boolean; message: CompanionMessage | null }>();
    eligibleSpaces.forEach((space, i) => {
      const result = welcomeQueries[i];
      map.set(space._id, {
        isLoading: result.isLoading,
        message: result.data?.messages?.[0] ?? null,
      });
    });
    return map;
  }, [welcomeQueries, eligibleSpaces]);

  // Computed fresh in the render body (not memoized) so the day-rollover
  // check below re-runs on every render, including the extra ones
  // `forceRecheck` triggers once a backgrounded tab/PWA regains focus.
  const today = localDateKey();

  const liveContent: ReflectionContent | null = useMemo(() => {
    if (eligibleSpaces.length === 0) return null;
    const synced = syncQueue(state, eligibleSpaces);
    const cur = currentReflection(synced, today);
    if (!cur) return null;

    // Never-wedge guard (Finding 1c). The true queue head (`cur.spaceId`)
    // is normally what gets shown, but its welcome may not exist YET —
    // either a brand-new space whose companion-generated welcome is still
    // in flight (fired fire-and-forget on space creation), or historically,
    // one that rotated out of a recency-limited fetch window (see the fetch
    // above). Rather than show nothing forever once that resolves to "no
    // welcome," walk forward through the queue — for DISPLAY ONLY, the
    // persisted DripState/queue order is never touched here — to the first
    // position whose welcome HAS resolved and been found, still honoring
    // "skipped today" along the way. The true head simply waits its turn:
    // once its welcome shows up, this same lookup surfaces it again from
    // the top on a later render. While any position along the walk is
    // still loading (not yet resolved either way), stop and return null
    // rather than guess past it — avoids flashing a stand-in card that
    // gets replaced a moment later once the real answer comes back.
    for (let i = 0; i < synced.queue.length; i++) {
      const id = synced.queue[i];
      if (synced.skippedOn[id] === today) continue; // "not today" honored — keep walking
      const w = welcomeBySpace.get(id);
      if (!w || w.isLoading) return null; // still resolving — no flash
      if (!w.message) continue; // resolved, genuinely no welcome yet — keep walking
      const space = eligibleSpaces.find((s) => s._id === id);
      if (!space) continue;
      const { framing, question } = extractQuestion(w.message.content ?? "");
      const nextId = synced.queue[i + 1] ?? null;
      const nextSpace = nextId ? eligibleSpaces.find((s) => s._id === nextId) : null;
      return {
        spaceId: id,
        spaceName: space.name,
        spaceColor: space.color || "#6366f1",
        identityLabel: space.identityLabel ?? null,
        framing,
        question,
        index: cur.index + i, // shifted from the true head's position by the walk
        total: cur.total,
        nextSpaceId: nextId,
        next: nextSpace
          ? { name: nextSpace.name, identityLabel: nextSpace.identityLabel ?? null }
          : null,
      };
    }
    return null; // no welcome resolved anywhere in the queue yet — show nothing rather than a blank card
  }, [eligibleSpaces, welcomeBySpace, state, today]);

  // Public content: an unexpired frozen post-answer snapshot takes priority
  // so the card can render its own saved state; otherwise the live drip
  // head. Recomputed directly in the render body (not memoized) so the day
  // check re-runs on every render, including the extra ones `forceRecheck`
  // triggers after a background tab regains focus past local midnight.
  const content =
    frozen && frozen.day === localDateKey() ? frozen.content : liveContent;

  const answer = useCallback(
    async (text: string): Promise<"insight" | "action"> => {
      if (!liveContent) return "insight";
      // Same AI classify as QuickInputCard — but a reflection is a ritual, so
      // AI unavailability quietly falls back to insight instead of dropping
      // into a manual type picker.
      let extracted: ExtractItemsResponse | null = null;
      try {
        extracted = await extractItems(retryFetch, {
          text, extractTypes: ["insight", "action"], language: i18n.language,
          context: liveContent.question ?? (liveContent.framing || undefined),
        });
      } catch {}
      const payload = buildReflectionTakeaway(text, liveContent, extracted);
      const saved = await createTakeaway(retryFetch, payload);
      const space = (spaces ?? []).find((s) => s._id === liveContent.spaceId);
      if (space?.status === "created") {
        changeSpaceStatus(retryFetch, liveContent.spaceId, "active").catch(() => {});
      }
      trackHomeEvent("reflection_answered", {
        area: liveContent.spaceName, day_index: liveContent.index, classified_as: payload.type,
      });
      setFrozen({ content: liveContent, day: localDateKey() });
      update(applyAnswer(syncQueue(state, spaces ?? []), liveContent.spaceId));
      // One-tap reclassify, mirroring the quick-capture toast.
      const savedId = saved?._id;
      if (savedId) {
        const other = payload.type === "insight" ? "action" : "insight";
        toast(t(payload.type === "insight" ? "capture_saved_insight" : "capture_saved_action"), {
          action: {
            label: t("capture_change_to", { type: t(other === "action" ? "capture_type_action" : "capture_type_insight") }),
            onClick: () =>
              editTakeaway(retryFetch, {
                id: savedId,
                type: other,
                dueDate: other === "action" ? dueInDays(0) : null,
              }).then(() => {
                queryClient.invalidateQueries({ queryKey: ["home-today-actions"] });
                queryClient.invalidateQueries({ queryKey: ["lifeos-roots"] });
              }).catch(() => {}),
          },
        });
      }
      if (payload.type === "action") {
        queryClient.invalidateQueries({ queryKey: ["home-today-actions"] });
      }
      return payload.type;
    },
    [liveContent, retryFetch, spaces, state, update, t, i18n.language]
  );

  const skip = useCallback(() => {
    if (!liveContent) return;
    trackHomeEvent("reflection_skipped", { area: liveContent.spaceName, day_index: liveContent.index });
    // Defer the walk-aware displayed next (liveContent.nextSpaceId), not the
    // literal queue front — an earlier head with no welcome yet may sit
    // between them in the persisted queue and must stay untouched.
    update(applySkip(syncQueue(state, spaces ?? []), liveContent.spaceId, localDateKey(), liveContent.nextSpaceId));
  }, [liveContent, spaces, state, update]);

  return { content, answer, skip };
}
